import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  useToast,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Spinner,
  useColorModeValue,
  ChakraProvider
} from '@chakra-ui/react';
import { cqAPI } from "../../lib/Auth";
import SignDocument from '../SignDocument/SignDocument';
import GenericSettings from "../GenericSettings/GenericSettings";
import FormItem from './FormItem';
import HistoryItem from './HistoryItem';
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ReactDOM from "react-dom/client";

const getParam = (p) => {
  const newSearchParams = new URLSearchParams(window.location.search);
  return newSearchParams.get(p);
};

const convertToGeneric = (template) => {
  return template?.map((section) => {
    return {
      title: section.title,
      description: section.description || "",
      inputs: section.questions.map((q) => ({
        type:
          q.type === "t"
            ? "input"
            : q.type === "yn"
            ? "switch"
            : q.type === "multiple"
            ? "select"
            : q.type === "ynwt"
            ? "switchwt"
            : q.type,
        options: q?.options?.length
          ? q?.options.map((option) => {
              if (typeof option === "string") {
                return { label: option, value: option };
              }
              return option;
            })
          : [],
        id: q.id,
        hint: q.hint,
        name: q.name || q.text,
        required: q.required,
      })),
    };
  });
};

function FormsAndHistory() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [targetTemplate, setTargetTemplate] = useState(null);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [selectedSignatureRequestId, setSelectedSignatureRequestId] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingMessage, setDownloadingMessage] = useState("");
  const [signerName, setSignerName] = useState("");
  const toast = useToast();
  const overlayBg = useColorModeValue("whiteAlpha.700", "blackAlpha.700");
  const cardBg = useColorModeValue("white", "gray.800");

  const bid = getParam("bid");
  const uid = getParam("_uid");
  const and_bid_uid = bid && uid ? `&bid=${bid}&_uid=${uid}` : "";
  const q_bid_uid = bid && uid ? `?bid=${bid}&_uid=${uid}` : "";

  const fetchAll = async () => {
    try {
      const [formsRes, historyRes] = await Promise.all([
        cqAPI.get('/pp/forms'),
        cqAPI.get(`/pp/history${q_bid_uid}`)
      ]);

      const forms = (formsRes.data || []).map(f => ({
        ...f,
        itemType: 'form',
        sortDate: new Date(f.created)
      }));

      const templates = (historyRes.data?.templates || []).map(t => ({
        ...t,
        itemType: 'history',
        sortDate: new Date(t.conversation_created || t.created)
      }));

      setSignerName(historyRes.data?.signer_name || "");

      const merged = [...forms, ...templates].sort((a, b) => b.sortDate - a.sortDate);
      setItems(merged);
    } catch (error) {
      console.error('Error fetching forms and history:', error);
    }
  };

  useEffect(() => {
    if (!view || view === "") {
      fetchAll();
    }
  }, [view]);

  // --- Form actions ---
  const handleSign = (signatureRequestId) => {
    setSelectedSignatureRequestId(signatureRequestId);
    setIsSignModalOpen(true);
  };

  const handleSignModalClose = () => {
    setIsSignModalOpen(false);
    setSelectedSignatureRequestId(null);
  };

  const handleSignatureSuccess = () => {
    setIsSignModalOpen(false);
    setSelectedSignatureRequestId(null);
    fetchAll();
  };

  const downloadFormFile = async (signatureRequestId, title) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(`/forms/download?request_id=${signatureRequestId}`);
      if (!downloadUrl) return;
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${title}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const cancelForm = async (signature_request_id) => {
    const response = await cqAPI.del(`/forms?request_id=${signature_request_id}`);
    if (response.type === 'ok') {
      fetchAll();
    } else if (response.type === 'error') {
      toast({ title: "Error", description: response.msg, status: "error" });
    }
  };

  // --- History actions ---
  const renderOffscreenForTemplate = (template, onReady) => {
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-99999px";
    container.style.top = "0";
    container.style.width = "794px";
    container.style.background = "#ffffff";
    container.style.overflow = "visible";
    container.style.padding = "32px";
    container.className = "print-root";
    document.body.appendChild(container);

    const style = document.createElement("style");
    style.textContent = `
      .print-root, .print-root * {
        transform: none !important;
        -webkit-transform: none !important;
        filter: none !important;
        overflow: visible !important;
      }
    `;
    container.appendChild(style);

    const root = ReactDOM.createRoot(container);

    const Capture = async () => {
      try {
        const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));
        await nextFrame();
        await nextFrame();
        if (document.fonts?.ready) {
          try { await document.fonts.ready; } catch {}
        }
        onReady({ container, root });
      } catch (e) {
        onReady({ container, root, error: e });
      }
    };

    root.render(
      <ChakraProvider>
        <Box p={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={2} textAlign="center">
            {template.name}
          </Text>
          <Flex direction="column" align="stretch" justify="flex-start" gap={6}>
            <GenericSettings
              description={template.description}
              configuration={convertToGeneric(template.data)}
              endpoint={`/pp/read-history/${template._id}`}
              bid_uid={and_bid_uid}
              long_query={true}
              supportBatching={true}
              viewOnly={true}
              printMode={true}
              onLoad={() => setTimeout(Capture, 0)}
            />
          </Flex>
        </Box>
      </ChakraProvider>
    );
  };

  const sendPDF = async (template) => {
    setDownloadingMessage("Saving PDF");
    setIsDownloading(true);
    try {
      renderOffscreenForTemplate(template, async ({ container, root, error }) => {
        if (error) {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
          return;
        }
        try {
          const canvas = await html2canvas(container, {
            scale: 1.5, useCORS: true, backgroundColor: "#ffffff",
            scrollX: 0, scrollY: 0,
            windowWidth: container.scrollWidth, windowHeight: container.scrollHeight,
          });
          const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const pageHeightPx = Math.floor((pageHeight / pageWidth) * canvas.width);
          let currentY = 0;
          let firstPage = true;
          while (currentY < canvas.height) {
            const sliceHeight = Math.min(pageHeightPx, canvas.height - currentY);
            const pageCanvas = document.createElement("canvas");
            pageCanvas.width = canvas.width;
            pageCanvas.height = sliceHeight;
            const ctx = pageCanvas.getContext("2d");
            ctx.drawImage(canvas, 0, currentY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
            const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.65);
            const pageImgHeightMm = (sliceHeight / canvas.width) * pageWidth;
            if (!firstPage) pdf.addPage();
            pdf.addImage(pageImgData, "JPEG", 0, 0, pageWidth, pageImgHeightMm, undefined, "FAST");
            firstPage = false;
            currentY += sliceHeight;
          }
          const dataUri = pdf.output("datauristring");
          const base64 = dataUri.split(",")[1] || "";
          await cqAPI.put(`/pp/history/${template._id}`, { file: base64 });
        } catch (err) {
          console.error("sendPDF failed", err);
        } finally {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
        }
      });
    } catch (outerErr) {
      console.error("sendPDF outer error", outerErr);
      setIsDownloading(false);
    }
  };

  const downloadHistory = async (template) => {
    try {
      setDownloadingMessage("Preparing your PDF");
      setIsDownloading(true);
      renderOffscreenForTemplate(template, async ({ container, root, error }) => {
        if (error) {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
          return;
        }
        try {
          const canvas = await html2canvas(container, {
            scale: 2, useCORS: true, backgroundColor: "#ffffff",
            scrollX: 0, scrollY: 0,
            windowWidth: container.scrollWidth, windowHeight: container.scrollHeight,
          });
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const imgProps = pdf.getImageProperties(imgData);
          const imgWidthMm = pageWidth;
          const imgHeightMm = (imgProps.height / imgProps.width) * imgWidthMm;
          let position = 0;
          let heightLeft = imgHeightMm;
          pdf.addImage(imgData, "PNG", 0, position, imgWidthMm, imgHeightMm);
          heightLeft -= pageHeight;
          position = -pageHeight;
          while (heightLeft > 0) {
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidthMm, imgHeightMm);
            heightLeft -= pageHeight;
            position -= pageHeight;
          }
          pdf.save(`${template.name || "history"}.pdf`);
        } finally {
          try { root.unmount(); } catch {}
          try { document.body.removeChild(container); } catch {}
          setIsDownloading(false);
        }
      });
    } catch (err) {
      console.error("Download failed", err);
      setIsDownloading(false);
    }
  };

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%" position="relative">
      {/* Modal for signing a document */}
      <Modal isOpen={isSignModalOpen} onClose={handleSignModalClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <SignDocument signatureRequestId={selectedSignatureRequestId} onSuccess={handleSignatureSuccess} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Combined list */}
      {!view && (
        <Box p={4} paddingBottom={0} width="100%" position="relative">
          <SimpleGrid columns={{ base: 1, md: 3, lg: 1 }}>
            {items.map((item, idx) =>
              item.itemType === 'form' ? (
                <FormItem
                  key={item._id}
                  form={item}
                  onSign={handleSign}
                  onDownload={downloadFormFile}
                  onCancel={cancelForm}
                  idx={idx}

                />
              ) : (
                <HistoryItem
                  key={`history-${item._id}`}
                  template={item}
                  onView={(tpl) => { setTargetTemplate(tpl); setView("view-completed"); }}
                  onComplete={(tpl) => { setTargetTemplate(tpl); setView("complete"); }}
                  onUpdate={(tpl) => { setTargetTemplate(tpl); setView("update"); }}
                  onDownload={downloadHistory}
                  idx={idx}
                  bid={bid}

                />
              )
            )}
          </SimpleGrid>
        </Box>
      )}

      {/* History detail views */}
      {targetTemplate && view === "view-completed" && (
        <GenericSettings
          description={targetTemplate.description}
          configuration={convertToGeneric(targetTemplate.data)}
          endpoint={`/pp/read-history/${targetTemplate._id}`}
          bid_uid={and_bid_uid}
          long_query={true}
          supportBatching={true}
          viewOnly={true}
          onClose={() => setView("")}
          toolbarText={signerName}
        />
      )}
      {targetTemplate && (view === "complete" || view === "update") && (
        <GenericSettings
          autoSave={true}
          saveButton={true}
          description={targetTemplate.description}
          configuration={convertToGeneric(targetTemplate.data)}
          endpoint={`/pp/history/${targetTemplate._id}`}
          bid_uid={and_bid_uid}
          long_query={true}
          supportBatching={true}
          onClose={(save) => {
            setView("");
            save && sendPDF(targetTemplate);
          }}
          toolbarText={signerName}
        />
      )}

      {/* Downloading overlay */}
      {isDownloading && (
        <Flex
          position="fixed" top={0} left={0} right={0} bottom={0}
          align="center" justify="center" bg={overlayBg} zIndex={9999}
        >
          <Box p={6} bg={cardBg} rounded="xl" boxShadow="xl"
            display="flex" flexDirection="column" alignItems="center" gap={3}
          >
            <Spinner size="xl" />
            <Text fontWeight="medium">{downloadingMessage}…</Text>
          </Box>
        </Flex>
      )}
    </Box>
  );
}

export default FormsAndHistory;
